import { FormikProvider, useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateShippingRateRequest } from '../../../common/model/dto/shipping-rate/create-shipping-rate-request';
import * as Yup from 'yup';
import styled from 'styled-components';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
} from '@mui/material';
import Colours from '../../../design-system/colours';
import breakpoints from '../../../design-system/breakpoints';
import OvTextField from '../atoms/OvTextField';
import OvButton from '../atoms/OvButton';
import Variables from '../../../design-system/variables';
import { formatPrice } from '../../../common/utils/helpers';
import OvSelect from '../atoms/OvSelect';
import { getCountriesSelectOptions } from '../../../common/model/dto/shipping-rate/countries-select-options';

interface OvCreateShippingRateDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (request: any) => void;
}

const OvCreateShippingRateDialog: FC<OvCreateShippingRateDialogProps> = ({
  isOpen,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();

  const formik = useFormik<CreateShippingRateRequest>({
    initialValues: {
      internal_name: '',
      display_name: '',
      description: '',
      currency: 'usd', // Hardcoded currency for now, later we can add a selector for this
      unit_amount: 0,
      delivery_estimate: {
        minimum: {
          unit: 'day',
          value: 1,
        },
        maximum: {
          unit: 'day',
          value: 2,
        },
      },
      is_active: false,
      countries: ['US'],
    },
    onSubmit: (values) => {
      onSave(values);
    },
    validationSchema: Yup.object().shape({
      internal_name: Yup.string().required('Internal name is required'),
      display_name: Yup.string().required('Display name is required'),
      description: Yup.string().required('Description is required'),
      unit_amount: Yup.number().required('Unit amount is required'),
    }),
  });

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const handleOnCancel = () => {
    onCancel();
    formik.resetForm();
  };

  return (
    <StyledDialog open={isOpen} onClose={handleOnCancel}>
      <FormikProvider value={formik}>
        <StyledDialogTitle>
          {t('shippingRates.dialogs.createShippingRateTitle')}
        </StyledDialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <StyledDialogContent>
            <StyledTextField
              type="text"
              fullWidth
              autoComplete="off"
              label={t('shippingRates.fields.internalName')}
              error={
                formik.touched.internal_name && !!formik.errors.internal_name
              }
              helperText={
                formik.errors.internal_name &&
                formik.touched.internal_name &&
                formik.errors.internal_name
              }
              {...formik.getFieldProps('internal_name')}
            />
            <StyledTextField
              type="text"
              fullWidth
              autoComplete="off"
              label={t('shippingRates.fields.displayName')}
              error={
                formik.touched.display_name && !!formik.errors.display_name
              }
              helperText={
                formik.errors.display_name &&
                formik.touched.display_name &&
                formik.errors.display_name
              }
              {...formik.getFieldProps('display_name')}
            />
            <StyledTextField
              type="text"
              fullWidth
              autoComplete="off"
              label={t('shippingRates.fields.description')}
              error={formik.touched.description && !!formik.errors.description}
              helperText={
                formik.errors.description &&
                formik.touched.description &&
                formik.errors.description
              }
              {...formik.getFieldProps('description')}
            />

            <StyledSwitchContainer>
              <FormControlLabel
                checked={formik.values.is_active}
                onChange={() =>
                  formik.setFieldValue('is_active', !formik.values.is_active)
                }
                control={<StyledSwitch />}
                label={t('physicalProducts.fields.isActive')}
                labelPlacement="top"
                style={{
                  marginLeft: '0',
                }}
              />
            </StyledSwitchContainer>

            <StyledTextField
              type="number"
              fullWidth
              autoComplete="off"
              label={t('physicalProducts.fields.unitAmount')}
              error={formik.touched.unit_amount && !!formik.errors.unit_amount}
              helperText={
                formik.errors.unit_amount &&
                formik.touched.unit_amount &&
                formik.errors.unit_amount
              }
              {...formik.getFieldProps('unit_amount')}
            />

            <StyledTextField
              type="text"
              fullWidth
              disabled
              autoComplete="off"
              label={t('physicalProducts.fields.displayPrice')}
              value={formatPrice(formik.values.unit_amount)}
            />

            <StyledText>
              {t('shippingRates.dialogs.targetCountries')}
            </StyledText>
            <StyledOvSelect
              displayEmpty
              {...formik.getFieldProps('countries')}
              multiple
            >
              {getCountriesSelectOptions().map((option) => (
                <StyledMenuItem key={option.value} value={option.value}>
                  {option.label}
                </StyledMenuItem>
              ))}
            </StyledOvSelect>

            <StyledDeliveryEstimateContainer>
              <StyledDeliveryEstimateLabel>
                {t('shippingRates.fields.deliveryEstimate')}
              </StyledDeliveryEstimateLabel>
              <StyledDeliveryEstimateRow>
                <StyledTextWrapper>
                  <StyledText>Between</StyledText>
                </StyledTextWrapper>
                <StyledInputWrapper>
                  <StyledTextField
                    type="number"
                    value={formik.values.delivery_estimate.minimum?.value}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'delivery_estimate.minimum.value',
                        Number(e.target.value)
                      )
                    }
                  />
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <StyledOvSelect
                    value={formik.values.delivery_estimate.minimum?.unit}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'delivery_estimate.minimum.unit',
                        e.target.value
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="day">
                      {t('shippingRates.dialogs.deliveryEstimateOptions.days')}
                    </MenuItem>
                    <MenuItem value="business_day">
                      {t(
                        'shippingRates.dialogs.deliveryEstimateOptions.businessDays'
                      )}
                    </MenuItem>
                    <MenuItem value="hour">
                      {t('shippingRates.dialogs.deliveryEstimateOptions.hours')}
                    </MenuItem>
                    <MenuItem value="month">
                      {t(
                        'shippingRates.dialogs.deliveryEstimateOptions.months'
                      )}
                    </MenuItem>
                    <MenuItem value="week">
                      {t('shippingRates.dialogs.deliveryEstimateOptions.weeks')}
                    </MenuItem>
                  </StyledOvSelect>
                </StyledInputWrapper>
              </StyledDeliveryEstimateRow>
              <StyledDeliveryEstimateRow>
                <StyledTextWrapper>
                  <StyledText>and</StyledText>
                </StyledTextWrapper>
                <StyledInputWrapper>
                  <StyledTextField
                    type="number"
                    value={formik.values.delivery_estimate.maximum?.value}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'delivery_estimate.maximum.value',
                        Number(e.target.value)
                      )
                    }
                  />
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <StyledOvSelect
                    value={formik.values.delivery_estimate.maximum?.unit}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'delivery_estimate.maximum.unit',
                        e.target.value
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="day">
                      {t('shippingRates.dialogs.deliveryEstimateOptions.days')}
                    </MenuItem>
                    <MenuItem value="business_day">
                      {t(
                        'shippingRates.dialogs.deliveryEstimateOptions.businessDays'
                      )}
                    </MenuItem>
                    <MenuItem value="hour">
                      {t('shippingRates.dialogs.deliveryEstimateOptions.hours')}
                    </MenuItem>
                    <MenuItem value="month">
                      {t(
                        'shippingRates.dialogs.deliveryEstimateOptions.months'
                      )}
                    </MenuItem>
                    <MenuItem value="week">
                      {t('shippingRates.dialogs.deliveryEstimateOptions.weeks')}
                    </MenuItem>
                  </StyledOvSelect>
                </StyledInputWrapper>
              </StyledDeliveryEstimateRow>
            </StyledDeliveryEstimateContainer>

            <StyledDialogActions>
              <StyledLightOvButton onClick={handleOnCancel}>
                {t('common.actions.cancel')}
              </StyledLightOvButton>
              <StyledOvButton type="submit">
                {t('common.actions.create')}
              </StyledOvButton>
            </StyledDialogActions>
          </StyledDialogContent>
        </form>
      </FormikProvider>
    </StyledDialog>
  );
};

export default OvCreateShippingRateDialog;

const StyledDialog = styled(Dialog)`
  && {
    .MuiDialog-paperScrollPaper {
      max-width: 100%;
      background-color: ${Colours.OV_WHITE};
      overflow: auto;
    }
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  && {
    text-align: center;
    font-weight: bold;
    text-transform: none;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const StyledTextField = styled(OvTextField)`
  && {
    flex: 1 0 100%;

    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }
    .MuiInputLabel-formControl {
      top: -0.2rem;
      opacity: 0.6;
    }
  }
`;

const StyledSwitchContainer = styled(Box)`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0;
  padding: 0 1rem 1rem 0.5rem;
`;

const StyledSwitch = styled(Switch)`
  && {
    .MuiSwitch-thumb {
      color: ${Colours.OV_BASE};
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 0 0.5rem 1.5rem;
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    max-width: fit-content;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.75rem;
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem;
    max-width: fit-content;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledText = styled.p`
  margin-top: 0.5rem;
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
`;

const StyledOvSelect = styled(OvSelect)`
  && {
    max-width: none;
    height: 3.1rem;

    &.MuiInputBase-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};
      width: 100%;

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }

      .MuiSelect-select {
        height: 100%;
        display: flex;
        align-items: center;
      }
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    &.MuiButtonBase-root {
      display: flex;
      justify-content: flex-start;
      padding: 0.375rem 1rem;
      font-size: ${Variables.fontSizes.MEDIUM};
    }
  }
`;

const StyledDeliveryEstimateContainer = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledDeliveryEstimateLabel = styled.p`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  margin-bottom: 1rem;
  flex: 1;
`;

const StyledDeliveryEstimateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StyledTextWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const StyledInputWrapper = styled.div`
  flex: 2;
  display: flex;
  align-items: center;
`;
